/**
 * Oturum yönetimi ve API çağrılarına kimlik ekleme.
 *
 * Panelde 230'u aşkın doğrudan fetch çağrısı var. Hepsine tek tek Authorization
 * başlığı eklemek yerine global fetch bir kez sarmalanıyor: API'ye giden her
 * istek token'ı otomatik taşıyor, 401 dönen her istek oturumu kapatıyor.
 */

export const API_BASE = "https://hiflowapi.stechomeyazilim.info";

const ACCESS_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "authRefreshToken";
const USER_KEY = "authUser";

/** Users_Type_ID karşılıkları. 3 = ADMIN. */
export const USER_TYPE = {
  CUSTOMER: 1,
  DRIVER: 2,
  ADMIN: 3,
  TOW_TRUCK: 4,
};

/** Panele girebilen kullanıcı tipleri. Bunun dışındaki bir tip tanınmaz sayılır. */
export const GECERLI_KULLANICI_TIPLERI = [
  USER_TYPE.CUSTOMER,
  USER_TYPE.DRIVER,
  USER_TYPE.ADMIN,
  USER_TYPE.TOW_TRUCK,
];

/**
 * Yalnızca yöneticinin görebileceği sayfalar.
 *
 * Kenar menüsü (SidebarContent.js) bu bağlantıları zaten role göre gizliyor, ama
 * gizlemek engellemek değildir: adresi bilen bir müşteri/sürücü doğrudan yazarak
 * girebilirdi. Liste menüdeki `isAdmin` bloklarıyla birebir aynı tutulmalıdır.
 *
 * Not: /borc-yonetimi bilerek listede değil — menüde hem yönetim bölümünde
 * ("Borç Yönetimi") hem de herkese açık "Ceza Ödemelerim" olarak geçiyor.
 */
export const YONETICI_ROTALARI = [
  "/projects-create",
  "/tasks-create",
  "/legal-documents",
  "/candidate-list",
  "/email-inbox",
  "/blog-list",
  "/apps-filemanager",
  "/crypto-wallet",
  "/surucu-hakedis",
  "/invoices-list",
  "/odemeler",
  "/fiyat-yonetimi",
  "/contacts-grid",
  "/contacts-list",
  "/contacts-profile",
];

export const yoneticiRotasiMi = (pathname) =>
  YONETICI_ROTALARI.some(
    (rota) => pathname === rota || pathname.startsWith(rota + "/")
  );

/* ------------------------------------------------------------------ */
/* Oturum deposu                                                       */
/* ------------------------------------------------------------------ */

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getAuthUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Kullanıcı nesnesini panelin beklediği PascalCase şekle çevirir.
 *
 * Panel kullanıcıyı eskiden OData'dan alıyordu; OData alan adlarını olduğu gibi
 * (`ID`, `Users_Type_ID`) döndürür. Yeni `/api/auth/...` uçları ise düz JSON
 * formatlayıcıdan geçiyor ve `CamelCasePropertyNamesContractResolver` alan
 * adlarının ilk harfini küçültüyor (`users_Type_ID`, `id`). Aradaki bu fark
 * yüzünden `authUser.Users_Type_ID` undefined kalıyordu.
 *
 * Her iki yazım da korunur, böylece OData'dan gelen nesneler de bozulmaz.
 */
export const normalizeUser = (user) => {
  if (!user || typeof user !== "object") return user;

  const out = { ...user };
  for (const [key, value] of Object.entries(user)) {
    // Camel-case çözümleyici yalnızca baştaki harfleri küçültür; tersine çeviriyoruz.
    const pascal = key === "id" ? "ID" : key.charAt(0).toUpperCase() + key.slice(1);
    if (!(pascal in out)) out[pascal] = value;
  }
  return out;
};

export const saveSession = ({ accessToken, refreshToken, user }) => {
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(normalizeUser(user)));
};

export const clearSession = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Token'ın süresi dolmuş mu.
 *
 * Bu yalnızca gereksiz istek atmamak için yapılan istemci tarafı bir ön kontroldür.
 * Asıl doğrulama sunucuda imza üzerinden yapılır — localStorage'daki değere
 * hiçbir yetki kararı dayandırılmaz.
 */
export const isTokenExpired = (token = getAccessToken()) => {
  if (!token) return true;
  try {
    const [, payload] = token.split(".");
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (!decoded.exp) return true;
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/** Geçerli bir oturum var mı (ön kontrol). */
export const hasValidSession = () => !!getAccessToken() && !isTokenExpired();

/* ------------------------------------------------------------------ */
/* Oturum sonlandırma                                                  */
/* ------------------------------------------------------------------ */

let redirecting = false;

/**
 * Oturumu kapatır ve giriş sayfasına yönlendirir.
 * Aynı anda birden fazla isteğin 401 dönmesi durumunda tek yönlendirme yapılır.
 */
export const forceLogout = (reason) => {
  clearSession();
  if (redirecting) return;
  redirecting = true;

  const target = reason
    ? `/login?reason=${encodeURIComponent(reason)}`
    : "/login";

  if (window.location.pathname !== "/login") {
    window.location.replace(target);
  } else {
    redirecting = false;
  }
};

/* ------------------------------------------------------------------ */
/* Token yenileme                                                      */
/* ------------------------------------------------------------------ */

let refreshPromise = null;

/**
 * Erişim token'ını yenileme token'ı ile tazeler.
 * Eşzamanlı çağrılar tek bir istek üzerinde bekler.
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        // Sarmalanmamış fetch kullanılır; aksi halde yenileme isteği kendini tetikler.
        const response = await originalFetch(`${API_BASE}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) return null;

        const data = await response.json();
        saveSession({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
        });
        return data.accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
};

/* ------------------------------------------------------------------ */
/* Global fetch sarmalayıcı                                            */
/* ------------------------------------------------------------------ */

const originalFetch = window.fetch.bind(window);

const isApiRequest = (input) => {
  const url =
    typeof input === "string" ? input : input && input.url ? input.url : "";
  return url.startsWith(API_BASE);
};

/** Token gerektirmeyen, hatta token'la çağrılmaması gereken uçlar. */
const isAuthEndpoint = (input) => {
  const url = typeof input === "string" ? input : input?.url || "";
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/register") ||
    url.includes("/api/auth/refresh") ||
    url.includes("/api/auth/otp/") ||
    url.includes("/api/auth/password/reset")
  );
};

/**
 * fetch'i bir kez sarmalar. Uygulama açılışında index.js'ten çağrılır.
 */
export const installAuthFetch = () => {
  if (window.__lojgoAuthFetchInstalled) return;
  window.__lojgoAuthFetchInstalled = true;

  window.fetch = async (input, init = {}) => {
    if (!isApiRequest(input) || isAuthEndpoint(input)) {
      return originalFetch(input, init);
    }

    const withToken = (token) => {
      const headers = new Headers(init.headers || {});
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return { ...init, headers };
    };

    let token = getAccessToken();

    // Süresi dolmuşsa istek atmadan önce yenilemeyi dene.
    if (token && isTokenExpired(token)) {
      token = (await refreshAccessToken()) || token;
    }

    let response = await originalFetch(input, withToken(token));

    // Sunucu reddettiyse bir kez yenileyip tekrar dene.
    if (response.status === 401) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        response = await originalFetch(input, withToken(fresh));
      }

      if (response.status === 401) {
        forceLogout("session-expired");
      }
    }

    return response;
  };
};

/* ------------------------------------------------------------------ */
/* Auth uçları                                                         */
/* ------------------------------------------------------------------ */

const postJson = async (path, body) => {
  const response = await originalFetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message =
      (data && (data.Message || data.message)) ||
      (typeof data === "string" ? data : "") ||
      text ||
      "İşlem başarısız oldu.";
    throw new Error(message);
  }

  return data;
};

export const loginRequest = (identifier, password) =>
  postJson("/api/auth/login", { identifier, password });

export const registerRequest = (payload) =>
  postJson("/api/auth/register", payload);

export const sendOtpRequest = (phoneNumber, purpose) =>
  postJson("/api/auth/otp/send", { phoneNumber, purpose });

export const verifyOtpRequest = (phoneNumber, code, purpose) =>
  postJson("/api/auth/otp/verify", { phoneNumber, code, purpose });

export const resetPasswordRequest = (phoneNumber, newPassword, verificationTicket) =>
  postJson("/api/auth/password/reset", {
    phoneNumber,
    newPassword,
    verificationTicket,
  });

/**
 * Oturum açmış kullanıcının kendi şifresini değiştirmesi.
 * Mevcut şifre sunucuda doğrulanır; yeni şifre hash'lenerek yazılır.
 * Bu istek token gerektirdiği için sarmalanmış fetch üzerinden gider.
 */
export const changePasswordRequest = async (currentPassword, newPassword) => {
  const response = await fetch(`${API_BASE}/api/auth/password/change`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || "Şifre güncellenemedi.");
  }
  return text ? JSON.parse(text) : { success: true };
};
